import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import { catchError, map, mergeMap, switchMap, take } from "rxjs/operators";

import { VendorSupportedProductsService } from "../../services/api/vendor-supported-products.service";
import { VendorSupportedProductsActions } from "../actions/vendor-supported-products.actions";
import { FailState } from "../models/fail-state";
import { AppState } from "../reducers";
import { VendorSupportedProductsSelectors } from "../selectors/vendor-supported-products.selectors";

@Injectable()
export class VendorSupportedProductsEffects {
	load = createEffect(() =>
		this.actions.pipe(
			ofType(VendorSupportedProductsActions.list.get),
			switchMap((action) =>
				this.store
					.select(VendorSupportedProductsSelectors.entry(action.vendorId))
					.pipe(
						take(1),
						map((entry) =>
							entry?.hasLoaded === true
								? VendorSupportedProductsActions.list.getSuccess({
										vendorId: action.vendorId,
										productIds: entry.productIds
									})
								: VendorSupportedProductsActions.list.getApi({
										vendorId: action.vendorId
									})
						)
					)
			)
		)
	);

	loadApi = createEffect(() =>
		this.actions.pipe(
			ofType(VendorSupportedProductsActions.list.getApi),
			switchMap((action) => {
				const request$ = action.vendorId
					? this.service.getForVendor(action.vendorId)
					: this.service.getForSelf();
				return request$.pipe(
					map((response) =>
						VendorSupportedProductsActions.list.getSuccess({
							vendorId: action.vendorId,
							productIds: response.productIds ?? []
						})
					),
					catchError((error) =>
						of(
							VendorSupportedProductsActions.list.getFail({
								...new FailState(error),
								vendorId: action.vendorId
							})
						)
					)
				);
			})
		)
	);

	save = createEffect(() =>
		this.actions.pipe(
			ofType(VendorSupportedProductsActions.save.put),
			mergeMap((action) => {
				const body = { productIds: action.productIds };
				const request$ = action.vendorId
					? this.service.putForVendor(action.vendorId, body)
					: this.service.putForSelf(body);
				return request$.pipe(
					map((response) =>
						VendorSupportedProductsActions.save.putSuccess({
							vendorId: action.vendorId,
							productIds: response.productIds ?? []
						})
					),
					catchError((error) =>
						of(
							VendorSupportedProductsActions.save.putFail({
								...new FailState(error),
								vendorId: action.vendorId
							})
						)
					)
				);
			})
		)
	);

	constructor(
		private actions: Actions,
		private service: VendorSupportedProductsService,
		private store: Store<AppState>
	) {}
}
